/**
 * Map whatever the vendor-portal preview DTO actually contains onto the two
 * flags the landing page needs.
 *
 * Open bid is on only when the RFQ/WO says so (`is_open_bid`, payload, or
 * `who_can_bid`). An explicit false means invited vendors only — the
 * new-vendor registration form must not show. `?openBid=1` on the hospital
 * share link can still turn it on when the DTO omitted the flag.
 */
export function coerceFlag(value) {
  if (value == null || value === '') return null;
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  const s = String(value).trim().toLowerCase();
  if (s === 'true' || s === '1' || s === 'yes' || s === 'on') return true;
  if (s === 'false' || s === '0' || s === 'no' || s === 'off') return false;
  return null;
}

const OPEN_BID_KEYS = ['is_open_bid', 'allow_unregistered', 'allow_unregistered_vendors', 'open_bid'];
const SEALED_KEYS = ['is_sealed_bid', 'sealed_bid'];

function collectFlags(obj, keys) {
  let sawTrue = false;
  let sawFalse = false;
  if (!obj || typeof obj !== 'object') return { sawTrue, sawFalse };
  keys.forEach((key) => {
    const parsed = coerceFlag(obj[key]);
    if (parsed === true) sawTrue = true;
    if (parsed === false) sawFalse = true;
  });
  return { sawTrue, sawFalse };
}

export function openBidQueryEnabled(search) {
  const q = new URLSearchParams(search || '');
  return q.get('openBid') === '1' || q.get('open_bid') === '1';
}

export function previewAllowsUnregistered(raw = {}, { queryOpen = false } = {}) {
  const nested = raw.rfq_payload && typeof raw.rfq_payload === 'object' ? raw.rfq_payload : {};
  const who = raw.who_can_bid || nested.who_can_bid;
  if (
    coerceFlag(raw.invite_only) === true
    || coerceFlag(nested.invite_only) === true
    || who === 'INVITED'
    || who === 'INVITED_ONLY'
  ) {
    return false;
  }
  if (who === 'OPEN' || who === 'OPEN_BID') return true;
  const top = collectFlags(raw, OPEN_BID_KEYS);
  const bag = collectFlags(nested, OPEN_BID_KEYS);
  if (top.sawTrue || bag.sawTrue) return true;
  // Hospital share links add ?openBid=1 when Open bid is on. That still
  // wins over a live DTO that defaulted the column to false.
  if (queryOpen) return true;
  return false;
}

export function previewIsSealed(raw = {}) {
  const nested = raw.rfq_payload && typeof raw.rfq_payload === 'object' ? raw.rfq_payload : {};
  const top = collectFlags(raw, SEALED_KEYS);
  const bag = collectFlags(nested, SEALED_KEYS);
  if (top.sawTrue || bag.sawTrue) return true;
  if (top.sawFalse || bag.sawFalse) return false;
  if (raw.bid_type === 'SEALED') return true;
  if (raw.bid_type === 'OPEN') return false;
  return false;
}

export function withOpenBidQuery(url, openBid) {
  if (!url || !openBid) return url || null;
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    parsed.searchParams.set('openBid', '1');
    return parsed.toString();
  } catch {
    return url;
  }
}

const HINT_STORAGE_PREFIX = 'hlthtek.vp.items.';

const GST_SLABS = [0, 1, 5, 12, 18, 28];

/** API tax fields are often "12.0000"; MUI select options are "12". */
export function normalizePortalGstPercent(value) {
  if (value === '' || value == null) return '';
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return '';
  const slab = GST_SLABS.find((s) => Math.abs(s - n) < 0.051);
  return slab != null ? String(slab) : String(n);
}

/** Prefer a real GST slab over a default/draft 0%. */
export function pickPortalGst(...values) {
  let zero = '';
  for (let i = 0; i < values.length; i += 1) {
    const normalized = normalizePortalGstPercent(values[i]);
    if (normalized === '') continue;
    if (normalized !== '0') return normalized;
    if (!zero) zero = normalized;
  }
  return zero;
}

function compactItemHints(items = []) {
  return (items || []).map((row) => ({
    id: row.item_id || row.rfq_item_id || row.id,
    n: portalItemLabel(row) || readable(row.item_name),
    h: portalItemHsn(row) || readable(row.hsn),
    u: portalItemUom(row) || readable(row.uom) || readable(row.uom_code),
    g: portalItemGst(row),
  })).filter((row) => row.id && row.n);
}

export function withItemHintsQuery(url, items) {
  if (!url) return url || null;
  const hints = compactItemHints(items);
  if (!hints.length) return url;
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const packed = btoa(encodeURIComponent(JSON.stringify(hints)));
    parsed.searchParams.set('ih', packed);
    if (parsed.toString().length > 1800) {
      parsed.searchParams.delete('ih');
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

export function itemHintsFromSearch(search) {
  const packed = new URLSearchParams(search || '').get('ih');
  if (!packed) return [];
  try {
    const rows = JSON.parse(decodeURIComponent(atob(packed)));
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

export function writePortalItemCache(rfqId, rfqNumber, items) {
  if (typeof localStorage === 'undefined') return;
  const hints = compactItemHints(items);
  if (!hints.length) return;
  try {
    if (rfqId) localStorage.setItem(HINT_STORAGE_PREFIX + rfqId, JSON.stringify(hints));
    if (rfqNumber) localStorage.setItem(HINT_STORAGE_PREFIX + rfqNumber, JSON.stringify(hints));
  } catch {
    /* quota / private mode */
  }
}

export function readPortalItemCache(rfqId, rfqNumber) {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = (rfqId && localStorage.getItem(HINT_STORAGE_PREFIX + rfqId))
      || (rfqNumber && localStorage.getItem(HINT_STORAGE_PREFIX + rfqNumber));
    const rows = raw ? JSON.parse(raw) : [];
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

export function applyItemHints(items = [], hints = []) {
  if (!hints.length) return items;
  const byId = {};
  hints.forEach((hint) => {
    if (hint?.id) byId[hint.id] = hint;
  });
  return items.map((row) => {
    const hint = byId[row.item_id] || byId[row.rfq_item_id] || byId[row.id];
    if (!hint) return row;
    return {
      ...row,
      item_name: portalItemLabel(row) || hint.n || row.item_name,
      hsn: portalItemHsn(row) || hint.h || row.hsn,
      uom: portalItemUom(row) || hint.u || row.uom,
      gst_rate: pickPortalGst(portalItemGst(row), hint.g, row.gst_rate),
      tax_percent: pickPortalGst(portalItemGst(row), hint.g, row.tax_percent),
    };
  });
}

function withMasterGst(row, master = {}) {
  const fromRow = portalItemGst(row);
  const fromMaster = portalItemGst(master);
  const gst = (fromRow && fromRow !== '0') ? fromRow : (fromMaster || fromRow || '');
  return {
    gst_rate: gst || row.gst_rate || master.gst_rate,
    tax_percent: gst || row.tax_percent,
  };
}

export function applyItemMasters(items = [], masters = {}) {
  return items.map((row) => {
    const master = masters[row.item_id] || masters[row.rfq_item_id] || {};
    if (portalItemLabel(row)) {
      return {
        ...row,
        item_name: portalItemLabel(row),
        hsn: portalItemHsn(row) || row.hsn,
        uom: portalItemUom(row) || row.uom,
        ...withMasterGst(row, master),
      };
    }
    const merged = { ...row, ...master, item: master.item || master };
    return {
      ...merged,
      item_name: portalItemLabel(merged),
      hsn: portalItemHsn(merged),
      uom: portalItemUom(merged),
      ...withMasterGst(merged, master),
    };
  });
}

export function previewNeedsItemNames(preview) {
  return (preview?.items || []).some((row) => !portalItemLabel(row));
}

export function previewNeedsItemGst(preview) {
  return (preview?.items || []).some((row) => {
    const gst = portalItemGst(row);
    return gst === '' || gst === '0';
  });
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuidLike(value) {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

function readable(value) {
  if (value == null) return '';
  const text = String(value).trim();
  if (!text || text === '-' || isUuidLike(text)) return '';
  return text;
}

function pickReadable(obj, keys) {
  if (!obj || typeof obj !== 'object') return '';
  for (let i = 0; i < keys.length; i += 1) {
    const hit = readable(obj[keys[i]]);
    if (hit) return hit;
  }
  return '';
}

const ITEM_NAME_KEYS = [
  'item_name', 'name', 'item_short_name', 'generic_name', 'product_name',
  'item_description', 'display_name', 'item_code', 'sku', 'description', 'title',
];

export function portalItemLabel(row = {}) {
  const nested = row.item || row.item_detail || row.catalogue_item || {};
  const payload = row.item_payload && typeof row.item_payload === 'object' ? row.item_payload : {};
  return pickReadable(row, ITEM_NAME_KEYS)
    || pickReadable(nested, ITEM_NAME_KEYS)
    || pickReadable(payload, ITEM_NAME_KEYS)
    || '';
}

export function portalItemHsn(row = {}) {
  const nested = row.item || {};
  const payload = row.item_payload || {};
  return pickReadable(row, ['hsn', 'hsn_code', 'hsn_sac'])
    || pickReadable(nested, ['hsn', 'hsn_code'])
    || pickReadable(payload, ['hsn', 'hsn_code'])
    || '';
}

export function portalItemGst(row = {}) {
  const nested = row.item || row.item_detail || {};
  const payload = row.item_payload && typeof row.item_payload === 'object' ? row.item_payload : {};
  const candidates = [
    row.tax_percent, row.gst_percent, row.gst_rate, row.gst,
    nested.tax_percent, nested.gst_percent, nested.gst_rate,
    payload.tax_percent, payload.gst_percent, payload.gst_rate,
  ];
  let zero = '';
  for (let i = 0; i < candidates.length; i += 1) {
    const normalized = normalizePortalGstPercent(candidates[i]);
    if (normalized === '') continue;
    if (normalized !== '0') return normalized;
    if (!zero) zero = normalized;
  }
  return zero;
}

export function portalItemUom(row = {}) {
  const nested = row.item || {};
  const payload = row.item_payload || {};
  return pickReadable(row, ['purchase_uom_code', 'uom', 'uom_code', 'uom_name', 'unit'])
    || pickReadable(nested, ['purchase_uom_code', 'uom', 'uom_code', 'base_uom_code', 'uom_name'])
    || pickReadable(payload, ['purchase_uom_code', 'uom', 'uom_code', 'uom_name'])
    || '';
}

export function portalVendorLabel(src = {}) {
  return pickReadable(src, [
    'vendor_name', 'name', 'business_name', 'legal_name', 'company_name',
    'contact_person', 'display_name',
  ]) || '';
}

export function shapeWorkOrderPreview(raw = {}) {
  const status = raw.status || '';
  const openBid = Boolean(raw.is_open_bid) && status === 'PUBLISHED';
  const items = (raw.items || []).map((it) => ({
    ...it,
    rfq_item_id: it.rfq_item_id || it.id || it.work_order_item_id,
    requested_quantity: it.requested_quantity ?? it.quantity ?? it.qty,
    unit_rate: it.unit_rate ?? it.rate,
    item_name: it.item_name || it.specification || '',
    uom: portalItemUom(it) || it.uom || it.uom_code || '',
    gst_rate: portalItemGst(it),
    tax_percent: portalItemGst(it),
  }));
  return {
    ...raw,
    kind: 'work_order',
    work_order_id: raw.work_order_id || raw.id,
    rfq_id: raw.work_order_id || raw.id,
    rfq_number: raw.wo_number || raw.rfq_number || '',
    rfq_title: raw.title || raw.rfq_title || '',
    is_open_bid: openBid,
    invite_only: !openBid,
    who_can_bid: openBid ? 'OPEN' : 'INVITED',
    accepting_quotes: status === 'PUBLISHED',
    accepting_response: status === 'ISSUED',
    items,
  };
}

export function normalizePortalPreview(raw = {}, opts = {}) {
  const payload = raw.rfq_payload && typeof raw.rfq_payload === 'object' ? raw.rfq_payload : {};
  const snapshots = [
    ...(Array.isArray(payload.portal_items) ? payload.portal_items : []),
    ...(Array.isArray(payload.items) ? payload.items : []),
  ];
  const byKey = {};
  snapshots.forEach((snap) => {
    ['rfq_item_id', 'id', 'item_id'].forEach((key) => {
      if (snap?.[key]) byKey[snap[key]] = snap;
    });
  });
  const items = (raw.items || []).map((row) => {
    const snap = byKey[row.rfq_item_id] || byKey[row.id] || byKey[row.item_id] || {};
    const merged = { ...snap, ...row };
    return {
      ...merged,
      rfq_item_id: row.rfq_item_id || row.id || snap.rfq_item_id || '',
      item_id: row.item_id || snap.item_id || '',
      uom_id: row.uom_id || snap.uom_id || '',
      requested_quantity: row.requested_quantity ?? row.required_quantity ?? row.qty
        ?? row.quantity ?? snap.requested_quantity,
      item_name: portalItemLabel(merged) || portalItemLabel(snap),
      hsn: portalItemHsn(merged) || portalItemHsn(snap),
      uom: portalItemUom(merged) || portalItemUom(snap),
      gst_rate: pickPortalGst(portalItemGst(merged), portalItemGst(snap)),
      tax_percent: pickPortalGst(portalItemGst(merged), portalItemGst(snap)),
    };
  });
  return {
    ...raw,
    rfq_id: raw.rfq_id || raw.id || '',
    rfq_number: pickReadable(raw, ['rfq_number', 'number']) || raw.rfq_number || '',
    is_open_bid: previewAllowsUnregistered(raw, opts),
    is_sealed_bid: previewIsSealed(raw),
    hospital_name: pickReadable(raw, ['hospital_name', 'facility_name', 'organisation_name', 'org_name']),
    department: pickReadable(raw, ['department', 'department_name', 'dept']),
    delivery_location: pickReadable(raw, [
      'delivery_location', 'store_name', 'delivery_store', 'store',
    ]),
    buyer_name: pickReadable(raw, [
      'buyer_name', 'requested_by_name', 'procurement_officer', 'contact_name', 'created_by_name',
    ]),
    buyer_role: pickReadable(raw, ['buyer_role', 'requested_by_role', 'procurement_officer_role']),
    items,
  };
}

export function portalBuyerContact(preview) {
  const name = pickReadable(preview || {}, [
    'buyer_name', 'requested_by_name', 'procurement_officer', 'contact_name',
  ]) || preview?.hospital_name || 'Procurement desk';
  const role = pickReadable(preview || {}, [
    'buyer_role', 'requested_by_role', 'procurement_officer_role',
  ]) || 'Procurement Officer';
  return { name, role };
}

function nestedId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') return value.id || value.rfq_item_id || value.item_id || '';
  return value;
}

export function quoteLineList(quote) {
  const src = quote && typeof quote === 'object' ? quote : {};
  const payload = src.quote_payload && typeof src.quote_payload === 'object' ? src.quote_payload : {};
  if (Array.isArray(src.lines) && src.lines.length) return src.lines;
  if (Array.isArray(src.quote_lines) && src.quote_lines.length) return src.quote_lines;
  if (Array.isArray(src.quote_items) && src.quote_items.length) return src.quote_items;
  if (Array.isArray(src.items) && src.items.length) return src.items;
  if (Array.isArray(payload.lines) && payload.lines.length) return payload.lines;
  if (Array.isArray(payload.items) && payload.items.length) return payload.items;
  return [];
}

export function normalizeQuoteLine(line = {}) {
  return {
    ...line,
    rfq_item_id: line.rfq_item_id || nestedId(line.rfq_item) || line.id || '',
    item_id: line.item_id || nestedId(line.item) || '',
    uom_id: line.uom_id || (isUuidLike(line.uom) ? line.uom : '') || '',
    quoted_quantity: line.quoted_quantity ?? line.qty ?? line.quantity,
    unit_rate: line.unit_rate ?? line.rate ?? line.quoted_rate ?? line.unit_price,
    discount_percent: line.discount_percent ?? line.discount ?? 0,
    tax_percent: normalizePortalGstPercent(
      line.tax_percent ?? line.gst_percent ?? line.gst,
    ) || '0',
    brand: line.brand || line.make || '',
    skipped: Boolean(line.skipped || line.not_quoted),
  };
}

export function quoteNetAmount(quote) {
  if (!quote || typeof quote !== 'object') return null;
  const keys = [
    'net_total', 'total_net_amount', 'total_value', 'grand_total',
    'net_amount', 'total_amount', 'net',
  ];
  for (let i = 0; i < keys.length; i += 1) {
    const raw = quote[keys[i]];
    if (raw == null || raw === '') continue;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function normalizePortalQuote(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const payload = src.quote_payload && typeof src.quote_payload === 'object' ? src.quote_payload : {};
  const lines = quoteLineList(src).map(normalizeQuoteLine);
  return {
    ...src,
    lines,
    net_total: quoteNetAmount(src),
    freight: src.freight ?? src.freight_amount ?? src.freight_charges ?? payload.freight ?? 0,
    insurance: src.insurance ?? src.insurance_amount ?? payload.insurance ?? 0,
    packing: src.packing ?? src.packing_amount ?? src.other_charges ?? payload.packing ?? payload.other_charges ?? 0,
    delivery_days: src.delivery_days ?? payload.delivery_days,
  };
}

export function asPortalList(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

export function quoteMatchesCurrentRfq(quote, preview) {
  if (!quote || !preview) return false;
  const previewKeys = [preview.rfq_id, preview.id, preview.rfq_number]
    .map((v) => String(v || '').trim())
    .filter(Boolean);
  const quoteKeys = [quote.rfq_id, quote.rfqId, quote.rfq_number, quote.rfq]
    .map((v) => String(v || '').trim())
    .filter(Boolean);
  return previewKeys.some((key) => quoteKeys.includes(key));
}

/** When my-rfqs/ is missing, still list the RFQ this public link opened. */
export function mergeCurrentRfqIntoDashboard(rfqList, preview, quoteList = []) {
  const list = asPortalList(rfqList);
  const quotes = asPortalList(quoteList);
  if (!preview?.rfq_id && !preview?.rfq_number && !preview?.id) return list;
  const already = list.some((row) => quoteMatchesCurrentRfq(
    { rfq_id: row.rfq_id, rfq_number: row.rfq_number },
    preview,
  ));
  if (already) return list;
  const quote = quotes.find((q) => quoteMatchesCurrentRfq(q, preview))
    || (quotes.length === 1 ? quotes[0] : null);
  let your_status = 'Not started';
  if (quote?.status === 'DRAFT') your_status = 'Draft saved';
  else if (quote?.status) your_status = 'Quote sent';
  return [
    {
      rfq_id: preview.rfq_id || preview.id || quotes[0]?.rfq_id,
      rfq_number: preview.rfq_number,
      rfq_title: preview.rfq_title,
      department: preview.department,
      items: (preview.items || []).length,
      submission_deadline: preview.submission_deadline,
      accepting_quotes: preview.accepting_quotes,
      your_status,
      is_sealed_bid: preview.is_sealed_bid,
      is_open_bid: preview.is_open_bid,
    },
    ...list,
  ];
}
